/**
 * PortfolioPage v5d — Diagnostic Tiles Edition
 *
 * Command center for pricing health: 4 diagnostic tiles, segmented control,
 * sort, search, drag-reorder, selection mode, recents carousel.
 * Integrates with existing VerdictSheet, SoldPriceModal, DeleteArtworkModal.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVerdicts } from '@/hooks/useVerdicts';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradeSheet } from '@/components/subscription/UpgradeSheet';
import { PriceCheckCounter } from '@/components/subscription/PriceCheckCounter';
import { trackEvent } from '@/services/telemetry/telemetry';
import { useDragReorder } from '@/hooks/useDragReorder';
import { getHealth, HEALTH_LABELS } from '@/lib/portfolioHealth';
import type { HealthStatus } from '@/lib/portfolioHealth';
import { computePortfolioHealth, toScoredVerdict, type ScoredVerdict } from '@/lib/portfolioHealth';
import { T } from '@/constants/portfolioTokens';
import { SlimHeader, HealthCard, PieceCard, PieceRow, GridCard, SearchOverlay, SalePriceSheet, MorphingBottomBar, DeleteConfirmSheet, SearchButton } from '@/components/portfolio';
import { KebabPopover } from '@/components/shared';
import { PortfolioSkeleton } from '@/components/ui/SkeletonScreen';
import { SkeletonRow } from '@/components/layout';
import { EmptyState } from '@/components/brand';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { VerdictBottomsheet } from '@/components/portfolio/VerdictBottomsheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SoldPriceModal } from '@/components/ui/SoldPriceModal';
import { Toast } from '@/components/ui/Toast';
import type { Verdict, VerdictSummary } from '@/types';

type SortId = 'date' | 'price' | 'hourly' | 'custom';

const SORTS: { id: SortId; label: string; asc: string; desc: string }[] = [
  { id: 'date', label: 'Date', asc: 'Date \u2191', desc: 'Date \u2193' },
  { id: 'price', label: 'Price', asc: 'Price \u2191', desc: 'Price \u2193' },
  { id: 'hourly', label: '$/hr', asc: '$/hr \u2191', desc: '$/hr \u2193' },
  { id: 'custom', label: 'Custom', asc: 'Custom', desc: 'Custom' },
];

/** Smooth scroll with quint ease-out — fast pickup, long silky deceleration */
function smoothScrollTo(container: HTMLElement, target: number, duration = 600) {
  const start = container.scrollTop;
  const distance = target - start;
  if (Math.abs(distance) < 2) return;
  const startTime = performance.now();
  const ease = (t: number) => 1 - Math.pow(1 - t, 5);
  function step(now: number) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    container.scrollTop = start + distance * ease(progress);
    if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

export function PortfolioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { isPremium, priceChecksUsed, priceChecksLimit } = useSubscription();
  const [upgradeSheetOpen, setUpgradeSheetOpen] = useState(false);
  const handlePriceAPiece = useCallback((from?: string) => {
    if (!isPremium && priceChecksUsed >= priceChecksLimit) {
      setUpgradeSheetOpen(true);
      return;
    }
    trackEvent({
      eventType: 'price_check_started',
      screen: 'portfolio',
      meta: { checkNumber: priceChecksUsed + 1, isPremium },
    });
    navigate('/app/calculate', from ? { state: { from } } : undefined);
  }, [isPremium, priceChecksUsed, priceChecksLimit, navigate]);
  const {
    summaries, isLoading, error, loadVerdicts,
    getVerdictById, removeVerdict, markVerdictSold, unmarkVerdictSold, updateVerdict,
  } = useVerdicts();

  // Floor hourly (cents/hr) from profile
  const floorHourly = useMemo(() => {
    if (!profile?.monthlyBurn || !profile?.monthlyCapacity) return 0;
    return profile.monthlyBurn / profile.monthlyCapacity;
  }, [profile]);

  const healthResult = useMemo(() => {
    if (summaries.length === 0) return null;
    const scored = summaries
      .map(p => toScoredVerdict({ sold: p.sold, soldPrice: p.soldPrice, inputs: { gutPrice: p.gutPrice }, calculations: { ttcp: p.ttcp } }))
      .filter((v): v is ScoredVerdict => v !== null);
    return computePortfolioHealth(scored);
  }, [summaries]);

  const score = healthResult?.score ?? 0;

  // ── Refs for scroll animation ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const healthCardRef = useRef<HTMLDivElement>(null);

  // ── Portfolio v5d state ──
  const [activeTile, setActiveTile] = useState<HealthStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'sold' | 'all'>('all');
  const [sort, setSort] = useState<SortId>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchBtnRef = useRef<HTMLDivElement>(null);
  const [searchOriginY, setSearchOriginY] = useState(100);
  const [searchOriginRight, setSearchOriginRight] = useState(20);
  const [selSearchQuery, setSelSearchQuery] = useState('');
  const [selMode, setSelMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState(false);
  const [customOrder, setCustomOrder] = useState<string[]>([]);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [sortOpen, setSortOpen] = useState(false);

  // ── Filter animation phase ──
  type FilterPhase = 'idle' | 'exiting' | 'hidden' | 'entering';
  const [filterPhase, setFilterPhase] = useState<FilterPhase>('idle');

  // ── Existing modal state ──
  const [selectedVerdict, setSelectedVerdict] = useState<Verdict | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [soldTarget, setSoldTarget] = useState<{ id: string; title: string; askingPrice: number } | null>(null);
  const [isSoldSaving, setIsSoldSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastUndo, setToastUndo] = useState<(() => void) | undefined>();

  // ── Kebab popover state ──
  const pageRef = useRef<HTMLDivElement>(null);
  const [contextTarget, setContextTarget] = useState<VerdictSummary | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ x: number; y: number; bottom: number }>({ x: 0, y: 0, bottom: 0 });

  // ── Sale price sheet state ──
  const [salePriceItems, setSalePriceItems] = useState<VerdictSummary[]>([]);


  // ── Delete confirm sheet state (batch 2+) ──
  const [deleteConfirmIds, setDeleteConfirmIds] = useState<string[]>([]);

  // ── Hidden IDs for optimistic delete with undo ──
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const deleteTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Tab counts for Active/Sold/All tabs
  const tabCounts = useMemo(() => {
    const nonDeleted = summaries.filter(p => !hiddenIds.has(p.id));
    return {
      active: nonDeleted.filter(p => !p.sold).length,
      sold: nonDeleted.filter(p => p.sold).length,
      all: nonDeleted.length,
    };
  }, [summaries, hiddenIds]);

  // ── Init ──
  useEffect(() => { setTimeout(() => setVisible(true), 150); }, []);

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!sortOpen) return;
    const handleClickOutside = () => { setSortOpen(false); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortOpen]);

  // ── Filter + sort ──
  const filtered = useMemo(() => {
    let list = [...summaries].filter(p => !hiddenIds.has(p.id));

    // Tab filter: Active / Sold / All
    if (activeTab === 'active') {
      list = list.filter(p => !p.sold);
    } else if (activeTab === 'sold') {
      list = list.filter(p => p.sold);
    }
    // 'all' = no filter

    // Health column filter (legacy diagnostic tile, if still active)
    if (activeTile) {
      list = list.filter(p => getHealth(p, floorHourly) === activeTile);
    }

    // Sort
    if (sort === 'custom' && customOrder.length > 0) {
      const orderMap = new Map(customOrder.map((id, i) => [id, i]));
      list.sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999));
    } else if (sort !== 'custom') {
      const dir = sortDir === 'asc' ? 1 : -1;
      list.sort((a, b) => {
        if (sort === 'price') {
          const priceA = a.sold && a.soldPrice ? a.soldPrice : (a.gutPrice ?? a.ttcp);
          const priceB = b.sold && b.soldPrice ? b.soldPrice : (b.gutPrice ?? b.ttcp);
          return (priceA - priceB) * dir;
        }
        if (sort === 'hourly') {
          const hA = a.effectiveHourly ?? -Infinity;
          const hB = b.effectiveHourly ?? -Infinity;
          return (hA - hB) * dir;
        }
        // date — newer dates have larger timestamps
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
      });
    }

    // Select-mode inline search filter
    if (selMode && selSearchQuery.trim()) {
      const q = selSearchQuery.toLowerCase();
      list = list.filter(p => {
        const title = (p.title || '').toLowerCase();
        const size = `${p.width}\u00D7${p.height}`;
        return title.includes(q) || size.includes(q);
      });
    }

    return list;
  }, [summaries, activeTab, activeTile, sort, sortDir, customOrder, floorHourly, hiddenIds, selMode, selSearchQuery]);

  // ── Sort handler ──
  const handleSort = useCallback((key: SortId) => {
    if (key === sort && key !== 'custom') {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(key);
      if (key !== 'custom') setSortDir('desc');
    }
  }, [sort]);

  // ── Filter animation style for card wrappers ──
  const filterCardStyle = useCallback((index: number): React.CSSProperties => {
    const i = Math.min(index, 9); // cap stagger at 10 cards
    switch (filterPhase) {
      case 'exiting':
        return {
          opacity: 0,
          transform: 'translateY(-8px)',
          transition: `opacity 180ms ease ${i * 15}ms, transform 180ms ease ${i * 15}ms`,
        };
      case 'hidden':
        return {
          opacity: 0,
          transform: 'translateY(8px)',
          transition: 'none',
        };
      case 'entering':
        return {
          opacity: 1,
          transform: 'translateY(0)',
          transition: `opacity 250ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 30}ms, transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 30}ms`,
        };
      default:
        return {};
    }
  }, [filterPhase]);

  // ── Filter switch animation — exit→swap→enter choreography ──
  const handleTile = useCallback((id: HealthStatus | null) => {
    if (filterPhase !== 'idle') return; // prevent double-tap

    const cardCount = Math.min(filtered.length, 10);
    const exitDuration = 180 + cardCount * 15;

    // Phase 1: Exit — cards slide up and fade out
    setFilterPhase('exiting');

    setTimeout(() => {
      // Phase 2: Swap data — instantly hide cards at enter position
      setActiveTile(id);
      setFilterPhase('hidden');

      // Phase 3: Enter — cards slide up from below with overshoot
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFilterPhase('entering');
          const enterDuration = 250 + 10 * 30;
          setTimeout(() => setFilterPhase('idle'), enterDuration);
        });
      });
    }, exitDuration);

    // Scroll health card to top on filter activation
    if (id !== null && scrollRef.current && healthCardRef.current) {
      const targetTop = healthCardRef.current.offsetTop;
      smoothScrollTo(scrollRef.current, targetTop, 600);
    }
  }, [filterPhase, filtered.length]);

  // ── Drag reorder ──
  const handleReorder = useCallback((from: number, to: number) => {
    if (sort !== 'custom') setSort('custom');
    // Build new order from current filtered list
    const ids = filtered.map(p => p.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    setCustomOrder(ids);
  }, [sort, filtered]);

  const { dragIdx, overIdx, startDrag, cardRefs } = useDragReorder(handleReorder);

  // ── Selection ──
  const toggleSelection = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      // Stay in select mode even with 0 selections — user exits via Cancel
      return next;
    });
  }, []);

  const enterSelection = useCallback((id: string) => {
    setSelMode(true);
    setSelected(new Set([id]));
  }, []);

  const selectAll = useCallback(() => {
    if (selected.size === filtered.length) {
      // Deselect all but stay in select mode
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(p => p.id)));
    }
  }, [filtered, selected.size]);

  const exitSelection = useCallback(() => {
    setSelMode(false);
    setSelected(new Set());
    setSearchOpen(false);
    setSelSearchQuery('');
  }, []);

  // ── Existing handlers ──
  const showToast = useCallback((msg: string, undoFn?: () => void) => {
    setToastMessage(msg);
    setToastUndo(() => undoFn);
    setToastVisible(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    setIsSheetOpen(false);
    setTimeout(() => setSelectedVerdict(null), 400);
  }, []);

  const handleRowClick = useCallback((verdictId: string) => {
    // Navigate to full-screen verdict detail page
    navigate(`/app/verdict/${verdictId}`);
  }, [navigate]);

  // ── Auto-open verdict sheet when navigated here with openVerdictId ──
  useEffect(() => {
    const openId = (location.state as { openVerdictId?: string })?.openVerdictId;
    if (openId) {
      window.history.replaceState({}, '');
      const t = setTimeout(() => handleRowClick(openId), 300);
      return () => clearTimeout(t);
    }
  }, [location.state, handleRowClick]);

  // ── Current index for pieceMeta (title display in sheet) ──
  const currentIndex = selectedVerdict
    ? filtered.findIndex(v => v.id === selectedVerdict.id)
    : -1;

  const handleDeleteArtwork = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await removeVerdict(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete artwork:', err);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, removeVerdict]);

  const handleMarkSold = useCallback(async (soldPrice: number, soldNote?: string, soldDate?: string, soldBuyer?: string, linkedTransactionId?: string | null) => {
    if (!soldTarget) return;
    setIsSoldSaving(true);
    try {
      const recoStatus = linkedTransactionId ? 'confirmed' as const : 'reported' as const;
      await markVerdictSold(soldTarget.id, soldPrice, soldNote, soldDate, soldBuyer, recoStatus, linkedTransactionId);
      // If linked, also write tpLinkedArtworkId on the transaction
      if (linkedTransactionId && user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const txnRef = doc(db, 'users', user.uid, 'transactions', linkedTransactionId);
        await updateDoc(txnRef, { tpLinkedArtworkId: soldTarget.id });
      }
      setSoldTarget(null);
      showToast('Sale recorded');
    } catch (err) {
      console.error('Failed to mark as sold:', err);
    } finally {
      setIsSoldSaving(false);
    }
  }, [soldTarget, markVerdictSold, showToast, user]);

  const handleUndoSold = useCallback(async (verdictId: string) => {
    try {
      await unmarkVerdictSold(verdictId);
      showToast('Sale removed');
    } catch (err) {
      console.error('Failed to undo sale:', err);
    }
  }, [unmarkVerdictSold, showToast]);

  // ── Kebab popover handler ──
  const handleKebab = useCallback((item: VerdictSummary, rect: DOMRect) => {
    setContextTarget(item);
    setKebabAnchor({ x: rect.right, y: rect.bottom, bottom: rect.bottom });
  }, []);

  // ── Swipe gesture handlers ──
  const handleSwipeDelete = useCallback((id: string) => {
    // Optimistically hide from UI
    setHiddenIds(prev => new Set(prev).add(id));

    // Schedule actual delete after undo window (5.5s > 5s toast duration)
    const timer = setTimeout(() => {
      removeVerdict(id).catch(console.error);
      setHiddenIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      deleteTimersRef.current.delete(id);
    }, 5500);
    deleteTimersRef.current.set(id, timer);

    showToast('Piece deleted', () => {
      // Undo: cancel pending delete, restore to UI
      const t = deleteTimersRef.current.get(id);
      if (t) clearTimeout(t);
      deleteTimersRef.current.delete(id);
      setHiddenIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    });
  }, [removeVerdict, showToast]);

  // ── Sale price sheet submit ──
  const handleSalePriceSubmit = useCallback(async (prices: Record<string, number>) => {
    const entries = Object.entries(prices);
    for (const [id, priceCents] of entries) {
      try {
        await markVerdictSold(id, priceCents);
      } catch (err) {
        console.error('Failed to mark as sold:', err);
      }
    }
    setSalePriceItems([]);
    showToast(entries.length === 1 ? 'Sale recorded' : `${entries.length} sales recorded`);
  }, [markVerdictSold, showToast]);

  // ── Batch delete confirm handler ──
  const handleBatchDeleteConfirm = useCallback(() => {
    const ids = deleteConfirmIds;
    // Optimistically hide all
    ids.forEach(id => {
      setHiddenIds(prev => new Set(prev).add(id));
      const timer = setTimeout(() => {
        removeVerdict(id).catch(console.error);
        setHiddenIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        deleteTimersRef.current.delete(id);
      }, 5500);
      deleteTimersRef.current.set(id, timer);
    });
    setDeleteConfirmIds([]);

    showToast(`${ids.length} pieces deleted`, () => {
      // Undo all
      ids.forEach(id => {
        const t = deleteTimersRef.current.get(id);
        if (t) clearTimeout(t);
        deleteTimersRef.current.delete(id);
        setHiddenIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      });
    });
  }, [deleteConfirmIds, removeVerdict, showToast, exitSelection]);

  // ── Quote confirm handler ──

  // ── Bulk actions (from morphing bottom bar) ──
  const handleBulkAction = useCallback((action: 'sold' | 'export' | 'delete') => {
    const ids = Array.from(selected);
    if (action === 'delete') {
      if (ids.length === 1) {
        // Single: undo toast, no confirmation
        handleSwipeDelete(ids[0]);
        exitSelection();
      } else {
        // Batch 2+: show confirmation sheet
        setDeleteConfirmIds(ids);
        exitSelection();
      }
    } else if (action === 'sold') {
      // Open SalePriceSheet for all selected unsold items
      const items = ids
        .map(id => summaries.find(p => p.id === id))
        .filter((p): p is VerdictSummary => !!p && !p.sold);
      if (items.length > 0) {
        setSalePriceItems(items);
      } else {
        showToast('No unsold pieces selected');
      }
      exitSelection();
    } else if (action === 'export') {
      showToast(`Exporting ${ids.length}\u2026`);
      exitSelection();
    }
  }, [selected, summaries, showToast, exitSelection, handleSwipeDelete]);



  // ── Loading state ──
  if (isLoading) return <PortfolioSkeleton />;

  // ── Error state ──
  if (error) {
    return (
      <div style={{
        minHeight: '100dvh', background: T.cream,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '0 24px', maxWidth: 420, margin: '0 auto', width: '100%',
      }}>
        <p style={{ fontSize: 18, color: T.text, fontWeight: 500, marginBottom: 16 }}>
          Unable to load. Retry.
        </p>
        <button
          onClick={() => loadVerdicts()}
          style={{ color: T.teal, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Empty state ──
  // Empty state is rendered inline within the real portfolio chrome — no early return.
  // This keeps the header (+), tabs, and controls visible even with zero items.

  // ── Main content ──
  return (
    <>
      <style>{`
        @keyframes popIn {
          from { opacity: 0; transform: translateX(-100%) scale(0.92); }
          to { opacity: 1; transform: translateX(-100%) scale(1); }
        }
        @keyframes menuPop {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes filterToastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .tp-card, .tp-card * {
          -webkit-user-select: none; user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>

      <div ref={pageRef} style={{
        width: '100%', maxWidth: 420, margin: '0 auto', minWidth: 0, background: T.cream,
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        overflowX: 'clip', position: 'relative',
        fontFamily: "'Manrope','DM Sans',sans-serif", WebkitFontSmoothing: 'antialiased',
      }}>
        {/* Fixed teal title bar */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          maxWidth: 420,
          margin: '0 auto',
          background: T.teal,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: 12,
          paddingLeft: 20,
          paddingRight: 20,
        }}>
          <h1 style={{
            fontSize: 20, fontWeight: 700, color: T.white, margin: 0,
            letterSpacing: '-0.01em',
            fontFamily: "'Manrope', 'DM Sans', sans-serif",
            WebkitFontSmoothing: 'antialiased' as const,
            flex: 1, textAlign: 'left',
          }}>Portfolio</h1>
          <PriceCheckCounter used={priceChecksUsed} limit={priceChecksLimit} isPremium={isPremium} />
          <button
            onClick={() => handlePriceAPiece()}
            aria-label="Price a piece"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              marginLeft: 8,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {/* Select-mode header (overlays teal header when active) */}
        {selMode && (
          <>
            <SlimHeader
              score={healthResult ? score : null}
              selMode={selMode}
              selCount={selected.size}
              totalFiltered={filtered.length}
              onSelectAll={selectAll}
              onExitSel={exitSelection}
              onSearchOpen={() => {
                setSearchOpen(s => !s);
                if (searchOpen) setSelSearchQuery('');
              }}
              searchOpen={searchOpen}
              selSearchQuery={selSearchQuery}
              onSelSearchQuery={setSelSearchQuery}
            />
            <div style={{ height: 64, flexShrink: 0 }} />
          </>
        )}

        {/* Scroll container below fixed header */}
        <div ref={scrollRef} style={{
          position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 50px)', left: 0, right: 0, bottom: 0,
          overflowY: 'auto', overflowX: 'hidden', minWidth: 0,
        }}>
          {/* Content block — no gap between header and tabs */}
          <div style={{
            background: T.cream,
            paddingTop: 0,
            position: 'relative',
            zIndex: 1,
            paddingBottom: 120,
          }}>
          {/* Tab bar — Active / Sold / All */}
          {!selMode && (
            <div style={{ padding: '12px 20px 0', background: T.white || '#FFFFFF' }}>
              <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.border03 || '#E8E5DE'}` }}>
                {(['active', 'sold', 'all'] as const).map(tab => {
                  const isActive = activeTab === tab;
                  const label = tab.charAt(0).toUpperCase() + tab.slice(1);
                  const count = tabCounts[tab];
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        flex: 1,
                        padding: '12px 0 10px',
                        background: 'none',
                        border: 'none',
                        borderBottom: isActive ? '2px solid #3DABA5' : '2px solid transparent',
                        cursor: 'pointer',
                        fontFamily: '"Manrope", "DM Sans", sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                      }}
                    >
                      <span style={{
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#3DABA5' : (T.textSec || '#A8A5A0'),
                      }}>
                        {label}
                      </span>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: isActive ? '#3DABA5' : '#C8C5BF',
                      }}>
                        ({count})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Controls row — sort pill LEFT, view toggles + search RIGHT */}
          {!selMode && (
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 20px 8px' }}>
              {/* Sort pill */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setSortOpen(o => !o); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 20,
                    border: `1.5px solid ${T.border03 || '#E8E5DE'}`,
                    background: T.white || '#FFFFFF',
                    cursor: 'pointer', fontFamily: "'Manrope', 'DM Sans', sans-serif",
                    fontSize: 12, fontWeight: 600, color: T.teal || '#3DABA5',
                  }}
                >
                  <span>Sort by: {SORTS.find(s => s.id === sort)?.label || 'Date'}</span>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {sortOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: 4,
                    background: '#FFFFFF', borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden', minWidth: 200, zIndex: 30,
                    animation: 'menuPop 150ms ease-out',
                  }}>
                    {SORTS.filter(s => s.id !== 'custom').map((s, si, arr) => {
                      const isActive = sort === s.id;
                      return (
                        <div key={s.id}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSort(s.id); setSortOpen(false); }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              width: '100%', padding: '12px 16px', border: 'none',
                              background: 'transparent',
                              color: '#1A1A1A',
                              fontSize: 14, fontWeight: isActive ? 600 : 400,
                              cursor: 'pointer', fontFamily: "'Manrope','DM Sans',sans-serif",
                            }}
                          >
                            <span>{s.label}</span>
                            {isActive && (
                              <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                <path d="M2 7l3.5 3.5L12 4" stroke="#3DABA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                          {si < arr.length - 1 && <div style={{ height: 1, background: '#F0EDE6', margin: '0 16px' }} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ flex: 1 }} />

              {/* View toggles + Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button onClick={() => setView('list')} aria-label="List view" style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: view === 'list' ? 'rgba(61,171,165,0.06)' : 'none',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M2 3h12M2 8h12M2 13h12" stroke={view === 'list' ? T.teal : T.textSec} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
                <button onClick={() => setView('grid')} aria-label="Grid view" style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: view === 'grid' ? 'rgba(61,171,165,0.06)' : 'none',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <rect x="1.5" y="1.5" width="5" height="5" rx="1" stroke={view === 'grid' ? T.teal : T.textSec} strokeWidth="1.3" />
                    <rect x="9.5" y="1.5" width="5" height="5" rx="1" stroke={view === 'grid' ? T.teal : T.textSec} strokeWidth="1.3" />
                    <rect x="1.5" y="9.5" width="5" height="5" rx="1" stroke={view === 'grid' ? T.teal : T.textSec} strokeWidth="1.3" />
                    <rect x="9.5" y="9.5" width="5" height="5" rx="1" stroke={view === 'grid' ? T.teal : T.textSec} strokeWidth="1.3" />
                  </svg>
                </button>
                <div ref={searchBtnRef}>
                  <SearchButton
                    isOpen={searchOpen}
                    onClick={() => {
                      if (!searchOpen && searchBtnRef.current) {
                        const rect = searchBtnRef.current.getBoundingClientRect();
                        setSearchOriginY(rect.top);
                        setSearchOriginRight(window.innerWidth - rect.right);
                      }
                      setSearchOpen(s => !s);
                      if (searchOpen) setSelSearchQuery('');
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Empty state — inside real portfolio chrome */}
          {summaries.length === 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              textAlign: 'center', padding: '60px 20px 0',
            }}>
              {/* Minimal easel icon */}
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true" style={{ marginBottom: 16, opacity: 0.35 }}>
                <rect x="10" y="6" width="28" height="22" rx="2" stroke="#3DABA5" strokeWidth="1.5" fill="none" />
                <line x1="24" y1="28" x2="24" y2="42" stroke="#3DABA5" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="16" y1="42" x2="32" y2="42" stroke="#3DABA5" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <div style={{
                fontSize: 16, fontWeight: 600,
                color: T.text, margin: '0 0 6px',
              }}>
                Your priced work lives here
              </div>
              <div style={{
                fontSize: 13, color: T.textSec || '#A8A5A0',
                lineHeight: 1.5, maxWidth: 220,
              }}>
                Each piece builds a record. One is all it takes to start.
              </div>
            </div>
          )}

          {/* Piece cards — list or grid */}
          {view === 'grid' ? (
            <div style={{
              padding: '8px 20px 0',
              boxSizing: 'border-box' as const,
            }}><div style={{
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 10, width: '100%', minWidth: 0,
            }}>
              {filtered.map((item, i) => (
                <div key={item.id} style={filterCardStyle(i)}>
                  <GridCard
                    item={item}
                    floorHourly={floorHourly}
                    currentBurn={profile?.monthlyBurn}
                    index={i}
                    visible={visible}
                    selectionMode={selMode}
                    selected={selected.has(item.id)}
                    onToggle={() => toggleSelection(item.id)}
                    onLongPress={() => enterSelection(item.id)}
                    onTap={() => handleRowClick(item.id)}
                  />
                </div>
              ))}
            </div></div>
          ) : (
            <div style={{ padding: '0 16px' }}>
              <div style={{
                background: T.white, borderRadius: 16,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
                overflow: 'hidden',
              }}>
                {filtered.map((item, i) => (
                  <div
                    key={item.id}
                    ref={el => { cardRefs.current[i] = el; }}
                    style={{
                      borderBottom: i < filtered.length - 1 ? '1px solid #E5E5E5' : 'none',
                      ...filterCardStyle(i),
                    }}
                  >
                    <div style={{ padding: '0 16px' }}>
                      <PieceCard
                        item={item}
                        floorHourly={floorHourly}
                        index={i}
                        visible={visible}
                        selectionMode={selMode}
                        selected={selected.has(item.id)}
                        onToggle={() => toggleSelection(item.id)}
                        onLongPress={() => enterSelection(item.id)}
                        onTap={() => handleRowClick(item.id)}
                        isDragging={dragIdx === i}
                        isDropTarget={overIdx === i && dragIdx !== i}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* "Price another piece" card — only when list has items */}
          {filtered.length > 0 && !selMode && (
            <div
              onClick={() => handlePriceAPiece()}
              style={{
                border: `1px dashed ${T.border03 || '#E8E5DE'}`,
                background: 'transparent',
                borderRadius: 16,
                padding: 16,
                margin: '8px 20px 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 16,
                background: T.tealBg || 'rgba(61,171,165,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke={T.teal || '#3DABA5'} strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <span style={{
                fontSize: 14, fontWeight: 500,
                color: T.teal || '#3DABA5',
                fontFamily: "'Manrope', 'DM Sans', sans-serif",
              }}>
                Price another piece
              </span>
            </div>
          )}

          {/* Empty state for filters — only when filters hide existing items */}
          {!filtered.length && summaries.length > 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              textAlign: 'center', padding: '48px 20px',
            }}>
              {/* Filter funnel SVG */}
              <svg width="80" height="80" viewBox="0 0 100 100" fill="none" aria-hidden="true">
                <path d="M20 24 H80 L56 54 V72 L44 78 V54 Z"
                  fill={T.div1} stroke={T.div2}
                  strokeWidth="1.5" strokeLinejoin="round" />
                <circle cx="50" cy="48" r="8"
                  fill={T.white} stroke={T.textMut}
                  strokeWidth="1.5" strokeDasharray="3 2" />
              </svg>
              <h3 style={{
                fontSize: 16, fontWeight: 700,
                color: T.text, margin: '16px 0 6px',
              }}>
                No pieces match this filter
              </h3>
              <p style={{
                fontSize: 13, color: T.textSec,
                lineHeight: 1.5, margin: '0 0 16px', maxWidth: 240,
              }}>
                Adjust your filter or clear it to see all pieces.
              </p>
              <button
                onClick={() => setActiveTile(null)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  color: T.teal, padding: 0,
                }}
              >
                Clear filter &rarr;
              </button>
            </div>
          )}
          </div>{/* end cream content block */}
        </div>{/* end scroll container */}

      </div>

      {/* Kebab floating popover */}
      {contextTarget && (
        <KebabPopover
          isSold={contextTarget.sold}
          anchorX={kebabAnchor.x}
          anchorY={kebabAnchor.y}
          anchorBottom={kebabAnchor.bottom}
          onClose={() => setContextTarget(null)}
          onAction={(key) => {
            const item = contextTarget;
            setContextTarget(null);
            switch (key) {
              case 'view': handleRowClick(item.id); break;
              case 'quote': navigate(`/app/quote/new/${item.id}`, { state: { fromRoute: '/app/portfolio' } }); break;
              case 'adjust': navigate(`/app/adjust/${item.id}`); break;
              case 'sold': setSalePriceItems([item]); break;
              case 'undo': handleUndoSold(item.id); break;
              case 'delete': handleSwipeDelete(item.id); break;
            }
          }}
        />
      )}

      {/* FAB — "Price a Piece" */}
      {!selMode && (
        <button
          onClick={() => handlePriceAPiece(location.pathname)}
          aria-label="Price a Piece"
          style={{
            position: 'fixed',
            bottom: 100,
            right: 20,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 20px',
            borderRadius: 26,
            border: 'none',
            background: T.teal,
            boxShadow: '0 4px 16px rgba(61,171,165,0.4)',
            cursor: 'pointer',
            zIndex: 50,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span style={{
            fontFamily: "'Manrope', 'DM Sans', -apple-system, sans-serif",
            fontSize: 14,
            fontWeight: 700,
            color: 'white',
            letterSpacing: '-0.01em',
          }}>
            Price a Piece
          </span>
        </button>
      )}

      {/* Sale price sheet */}
      {salePriceItems.length > 0 && (
        <SalePriceSheet
          artworks={salePriceItems}
          onSubmit={handleSalePriceSubmit}
          onClose={() => setSalePriceItems([])}
        />
      )}


      {/* Delete confirmation sheet (batch 2+) */}
      {deleteConfirmIds.length > 0 && (
        <DeleteConfirmSheet
          count={deleteConfirmIds.length}
          onConfirm={handleBatchDeleteConfirm}
          onClose={() => setDeleteConfirmIds([])}
        />
      )}

      {/* Morphing bottom bar — portaled to body to escape SwipeablePageContainer's
           transform stacking context (transform: translateX creates a containing block
           that traps position:fixed children, making z-index ineffective vs CanvasBottomNav) */}
      {createPortal(
        <MorphingBottomBar
          selectMode={selMode}
          selCount={selected.size}
          onMarkSold={() => handleBulkAction('sold')}
          onDelete={() => handleBulkAction('delete')}
        />,
        document.body,
      )}

      {/* Search overlay — only in normal mode (select mode uses inline pill search) */}
      <SearchOverlay
        isOpen={searchOpen && !selMode}
        onClose={() => setSearchOpen(false)}
        summaries={summaries}
        floorHourly={floorHourly}
        onTapItem={(id) => { setSearchOpen(false); handleRowClick(id); }}
        originY={searchOriginY}
        originRight={searchOriginRight}
      />

      {/* Bottom Sheet for verdict detail */}
      <BottomSheet isOpen={isSheetOpen} onClose={handleSheetClose} variant="verdict">
        {selectedVerdict && (
          <VerdictBottomsheet
            verdict={selectedVerdict}
            userId={user?.uid}
            profile={profile}
            onRePrice={(v) => {
              setIsSheetOpen(false);
              setTimeout(() => navigate(`/app/adjust/${v.id}`), 300);
            }}
            onCreateQuote={(v) => {
              setIsSheetOpen(false);
              setTimeout(() => navigate(`/app/quote/new/${v.id}`, { state: { fromRoute: '/app/portfolio' } }), 300);
            }}
            onMarkSold={(v) => {
              setIsSheetOpen(false);
              setTimeout(() => setSoldTarget({
                id: v.id,
                title: v.inputs.title || 'Untitled',
                askingPrice: v.inputs.gutPrice ?? 0,
              }), 300);
            }}
            onUndoSold={(v) => {
              setIsSheetOpen(false);
              setTimeout(() => handleUndoSold(v.id), 300);
            }}
            onDelete={(v) => {
              setIsSheetOpen(false);
              setTimeout(() => setDeleteTarget({
                id: v.id,
                title: v.inputs.title || 'Untitled',
              }), 300);
            }}
            onConfirmManual={async (v) => {
              await updateVerdict(v.id, { reconciliationStatus: 'confirmed_manual' });
            }}
          />
        )}
      </BottomSheet>

      {/* Delete confirmation modal */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteArtwork}
        title="Delete this artwork?"
        description={`"${deleteTarget?.title || 'Untitled'}" and all linked pricing data will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        isLoading={isDeleting}
      />

      {/* Sold price modal */}
      <SoldPriceModal
        isOpen={!!soldTarget}
        onClose={() => setSoldTarget(null)}
        onConfirm={handleMarkSold}
        artworkTitle={soldTarget?.title || 'Untitled'}
        askingPrice={soldTarget?.askingPrice ?? 0}
        isSaving={isSoldSaving}
        userId={user?.uid}
      />

      {/* Health filter toast — dark pill above bottom nav */}
      {activeTile && (
        <div style={{
          position: 'fixed', bottom: 90, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          zIndex: 40, pointerEvents: 'none',
          animation: 'filterToastIn 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}>
          <div style={{
            background: T.text, borderRadius: 16,
            padding: '8px 14px 8px 16px',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
          }}>
            <span style={{
              fontSize: 12, fontWeight: 500, color: T.white,
              fontFamily: "'Manrope','DM Sans',sans-serif",
            }}>
              Showing: {HEALTH_LABELS[activeTile]}
            </span>
            <button
              onClick={() => handleTile(null)}
              style={{
                background: 'rgba(255,255,255,0.12)', border: 'none',
                borderRadius: 12, padding: '3px 10px',
                fontSize: 11, fontWeight: 600, color: T.white,
                cursor: 'pointer', fontFamily: "'Manrope','DM Sans',sans-serif",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        message={toastMessage}
        isVisible={toastVisible}
        onDismiss={() => { setToastVisible(false); setToastUndo(undefined); }}
        onUndo={toastUndo}
      />
      <UpgradeSheet isOpen={upgradeSheetOpen} onClose={() => setUpgradeSheetOpen(false)} trigger="limit" />
    </>
  );
}

export default PortfolioPage;
